import { AlertTriangle, CheckCircle2, RefreshCw, Settings, Layers, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * HealthAlertBanner
 * Sticky banner shown at top of dashboard when SMTP or widgets have problems.
 * Self-collapsing when everything is healthy.
 */
export default function HealthAlertBanner({ status, onRunChecks, isRunning }) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  if (!status) return null;
  const smtp = status.smtp || {};
  const widgets = status.widgets || {};

  // Decide severity
  const smtpProblem = (() => {
    if (!smtp.enabled) return { severity: 'info', label: 'SMTP désactivé', detail: 'Vos clients reçoivent les emails depuis l\'adresse globale ResaWork — pour envoyer depuis votre propre adresse, activez et configurez votre SMTP.' };
    if (smtp.enabled && !smtp.configured) return { severity: 'error', label: 'SMTP incomplet', detail: 'Activez et complétez tous les champs SMTP (serveur, utilisateur, mot de passe, expéditeur).' };
    if (smtp.last_test_success === false) return { severity: 'error', label: 'SMTP en échec', detail: smtp.last_test_error || 'Le dernier test SMTP a échoué.' };
    if (smtp.last_send_success === false) return { severity: 'warning', label: 'Dernier envoi en échec', detail: 'Le dernier email envoyé n\'a pas pu partir. Vérifiez la configuration.' };
    return null;
  })();
  const widgetsProblem = widgets.broken_count > 0
    ? { severity: 'error', label: `${widgets.broken_count} widget${widgets.broken_count > 1 ? 's' : ''} inaccessible${widgets.broken_count > 1 ? 's' : ''}`, detail: 'Au moins un widget public renvoie une erreur. Vos clients ne peuvent plus l\'utiliser.' }
    : null;

  const hasProblem = !!smtpProblem || !!widgetsProblem;
  if (!hasProblem) {
    // No problem → tiny success bar (optional, can be hidden)
    if (collapsed) return null;
    return (
      <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-center gap-2 text-xs text-emerald-800" data-testid="health-banner-ok">
        <CheckCircle2 className="w-4 h-4 text-emerald-700" />
        <span className="font-medium">Tout est opérationnel</span>
        <span className="text-emerald-700/70">— SMTP {smtp.enabled ? 'actif' : 'non configuré'} · {widgets.healthy}/{widgets.active} widgets vérifiés</span>
        <button
          type="button"
          onClick={onRunChecks}
          disabled={isRunning}
          className="ml-auto text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
          data-testid="health-banner-recheck"
        >
          <RefreshCw className={`w-3 h-3 ${isRunning ? 'animate-spin' : ''}`} />
          Re-tester
        </button>
        <button type="button" onClick={() => setCollapsed(true)} className="text-emerald-700/60 hover:text-emerald-900 ml-2" data-testid="health-banner-collapse">×</button>
      </div>
    );
  }

  // Resolve top severity
  const topSeverity = (smtpProblem?.severity === 'error' || widgetsProblem?.severity === 'error') ? 'error'
    : (smtpProblem?.severity === 'warning' ? 'warning' : 'info');
  const styles = {
    error: { box: 'border-rose-300 bg-rose-50', icon: 'text-rose-700', title: 'text-rose-900', text: 'text-rose-800' },
    warning: { box: 'border-amber-300 bg-amber-50', icon: 'text-amber-700', title: 'text-amber-900', text: 'text-amber-800' },
    info: { box: 'border-sky-300 bg-sky-50', icon: 'text-sky-700', title: 'text-sky-900', text: 'text-sky-800' },
  }[topSeverity];

  return (
    <div className={`mb-4 rounded-xl border ${styles.box} px-4 py-3`} data-testid="health-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 ${styles.icon} mt-0.5 shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${styles.title}`}>
            {topSeverity === 'error' ? 'Problèmes détectés sur votre installation' : (topSeverity === 'warning' ? 'Attention requise' : 'Configuration à compléter')}
          </p>
          <div className="mt-2 space-y-2">
            {smtpProblem && (
              <div className="flex items-start gap-2" data-testid="health-banner-smtp">
                <Settings className={`w-4 h-4 ${styles.icon} mt-0.5 shrink-0`} />
                <div className="flex-1">
                  <p className={`text-sm font-medium ${styles.text}`}>{smtpProblem.label}</p>
                  <p className={`text-xs ${styles.text} opacity-90`}>{smtpProblem.detail}</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/company/settings?tab=smtp')}
                  className="text-xs font-medium underline hover:no-underline whitespace-nowrap"
                  data-testid="health-banner-smtp-cta"
                >
                  Configurer <ChevronRight className="inline w-3 h-3" />
                </button>
              </div>
            )}
            {widgetsProblem && (
              <div className="flex items-start gap-2" data-testid="health-banner-widgets">
                <Layers className={`w-4 h-4 ${styles.icon} mt-0.5 shrink-0`} />
                <div className="flex-1">
                  <p className={`text-sm font-medium ${styles.text}`}>{widgetsProblem.label}</p>
                  <p className={`text-xs ${styles.text} opacity-90`}>{widgetsProblem.detail}</p>
                  {widgets.broken && widgets.broken.length > 0 && (
                    <ul className="mt-1 text-xs space-y-0.5">
                      {widgets.broken.slice(0, 3).map((b) => (
                        <li key={b.widget_id} className={`${styles.text} opacity-90`}>
                          • {b.name || b.widget_id} <span className="opacity-70">({b.error})</span>
                        </li>
                      ))}
                      {widgets.broken.length > 3 && (
                        <li className={`${styles.text} opacity-70`}>+ {widgets.broken.length - 3} autre{widgets.broken.length - 3 > 1 ? 's' : ''}</li>
                      )}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/company/widgets')}
                  className="text-xs font-medium underline hover:no-underline whitespace-nowrap"
                  data-testid="health-banner-widgets-cta"
                >
                  Voir les widgets <ChevronRight className="inline w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onRunChecks}
          disabled={isRunning}
          className={`text-xs ${styles.text} inline-flex items-center gap-1 px-2 py-1 rounded-md border border-current/30 hover:bg-white/40 whitespace-nowrap`}
          data-testid="health-banner-rerun"
        >
          <RefreshCw className={`w-3 h-3 ${isRunning ? 'animate-spin' : ''}`} />
          Re-tester
        </button>
      </div>
    </div>
  );
}
